import Text from '@components/Text/Text'
import { TextTag } from '@enums/textTag'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { VelvetSDK } from 'sdk'
import './CreateNewProfile.scss'
import Input from '@components/Input/Input'
import { SecondaryButton } from '@components/Buttons/SecondaryButton/SecondaryButton'
import { useNavigate } from 'react-router-dom'
import { routes } from '@constants/routes.const'
import { defaultProfileImage, UserProfile } from 'sdk/services/storage/profiles/types/profilesManager'
import { useTypedSelector } from 'store'
import { getActiveProfile, getLetterImageTypeData, isClassicsImage } from '@utils/helpers'
import { ProfileImagesData } from 'sdk/services/config/types/config'
import { language } from '@constants/lang.const'
import { ErrorActions } from '@constants/index'
import { useDispatch } from 'react-redux'
import { setError } from '@actions/errorCodeActions'
import DotSpinner from '@components/DotSpinner/DotSpinner'
import classNames from 'classnames'

interface CreateNewProfileProps {
  showMenu: (status: boolean) => void
}

export const CreateNewProfile = ({ showMenu }: CreateNewProfileProps) => {
  const V = VelvetSDK.Vocabulary.vocabulary
  const inputRef = useRef<HTMLInputElement>(null)
  const [inputValue, setInputValue] = useState('')
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const profiles = useTypedSelector((state) => state.profile.profiles)
  const [availableClassicsImages, setAvailableClassicsImages] = useState<ProfileImagesData[]>([])
  const profileImages = VelvetSDK.Configuration.configuration.profileImages
  const [isLoading, setIsLoading] = useState(false)
  const activeProfile = getActiveProfile()

  const handleCancelButton = useCallback(() => {
    navigate(routes.MANAGE_PROFILES)
  }, [navigate])

  const handleDoneButton = useCallback(() => {
    const createProfile = async () => {
      try {
        setIsLoading(true)

        await VelvetSDK.Storage.Profiles.service.createProfile({
          name: inputValue.trim(),
          lang: activeProfile?.preferredLanguage || language.EN,
          image: availableClassicsImages?.[0] ?? defaultProfileImage,
        })
      } catch (error) {
        console.error('Failed to create profile:', error)
        setIsLoading(false)
        const errorUpdate = {
          showErrorModal: true,
          fetchAction: ErrorActions.CREATE_PROFILE,
        }
        dispatch(setError(errorUpdate))
      }
    }
    createProfile().then(() => {
      setIsLoading(false)
      navigate(routes.MANAGE_PROFILES)
    })
  }, [availableClassicsImages, dispatch, inputValue, navigate, activeProfile?.preferredLanguage])

  useEffect(() => {
    showMenu(false)
    inputRef.current && inputRef.current.focus()
  }, [showMenu])

  useEffect(() => {
    if (profiles && profileImages) {
      const profilesThatUseClassicsImage = profiles.filter(isClassicsImage)

      const arrayOfUsedClassicsSlugs = profilesThatUseClassicsImage.map((profile: UserProfile) => profile.image?.slug)

      const arrayOfAllClassicsImages = getLetterImageTypeData(profileImages)

      if (arrayOfAllClassicsImages) {
        const arrayOfFilteredClassicsImages = arrayOfAllClassicsImages?.filter(
          (image) => !arrayOfUsedClassicsSlugs.includes(image?.slug),
        )
        setAvailableClassicsImages(arrayOfFilteredClassicsImages)
      }
    }
  }, [profileImages, profiles])

  return (
    <div className="add-new-profile-page">
      <div className={classNames('add-new-profile-wrapper', isLoading && 'is-loading')}>
        <Text tag={TextTag.H1}>{V.profileManagementAddAprofile}</Text>
        <Text tag={TextTag.P}>{V.profileManagementCreateProfileText}</Text>
        <div className="input">
          <Input
            type="text"
            placeholder={V.profileManagementEnterName}
            className="focusable"
            inputRef={inputRef}
            onChange={(e) => setInputValue(e.target.value)}
          />
        </div>
        <div className="buttons">
          <SecondaryButton disabled={inputValue.trim().length === 0} onClick={handleDoneButton}>
            {V.genericDoneLabel}
          </SecondaryButton>
          <SecondaryButton onClick={handleCancelButton}>{V.genericCancelLabel}</SecondaryButton>
        </div>
      </div>
      {isLoading && <DotSpinner />}
    </div>
  )
}
